import { create } from 'zustand';

type CorePanel =
  | 'explorer'
  | 'search'
  | 'deploy'
  | 'ai'
  | 'settings'
  | 'debug'
  | 'health'
  | 'wallet'
  | 'git'
  | 'contracts'
  | 'extensions'
  | 'secrets'
  | 'logs'
  | 'ai-agent';
/** Active panel can be a built-in panel or an extension panel (prefixed with 'ext:') */
type ActivePanel = CorePanel | `ext:${string}`;

export interface DialogState {
  title: string;
  message: string;
  buttons: string[];
  defaultButton?: string;
}

interface UIState {
  sidebarWidth: number;
  terminalHeight: number;
  showPreview: boolean;
  showTerminal: boolean;
  theme: 'dark';
  activePanel: ActivePanel;

  // Extension dialog system
  dialog: DialogState | null;
  dialogResolve: ((value: string | null) => void) | null;

  setSidebarWidth: (width: number) => void;
  setTerminalHeight: (height: number) => void;
  setShowPreview: (show: boolean) => void;
  setShowTerminal: (show: boolean) => void;
  togglePreview: () => void;
  toggleTerminal: () => void;
  setActivePanel: (panel: ActivePanel) => void;
  showDialog: (state: DialogState) => Promise<string | null>;
  closeDialog: (result: string | null) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarWidth: 260,
  terminalHeight: 200,
  showPreview: false,
  showTerminal: true,
  theme: 'dark',
  activePanel: 'explorer',

  dialog: null,
  dialogResolve: null,

  setSidebarWidth: (width) => set({ sidebarWidth: Math.max(180, Math.min(500, width)) }),
  setTerminalHeight: (height) => set({ terminalHeight: Math.max(100, Math.min(600, height)) }),
  setShowPreview: (show) => set({ showPreview: show }),
  setShowTerminal: (show) => set({ showTerminal: show }),
  togglePreview: () => set((s) => ({ showPreview: !s.showPreview })),
  toggleTerminal: () => set((s) => ({ showTerminal: !s.showTerminal })),
  setActivePanel: (panel) => set({ activePanel: panel }),

  showDialog: (state) => {
    return new Promise<string | null>((resolve) => {
      set({ dialog: state, dialogResolve: resolve });
    });
  },

  closeDialog: (result) => {
    const { dialogResolve } = get();
    if (dialogResolve) dialogResolve(result);
    set({ dialog: null, dialogResolve: null });
  },
}));
